import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';

import { loadCommunicationTemplates } from '../../../store/communicationService';
import {
    loadContractDetailsForContractId,
    loadFormStatusDetails,
    loadFormTypes,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import { loadForm, loadFormContractByContract, loadForms } from '../../../store/participantService';
import { selectUsersForNames } from '../../../store/userSelectors';
import {
    loadUsersMinimalDetails,
    searchUsersByLoggedInUserServiceIds
} from '../../../store/userService';
import LoadingSpinner from '../../ui/LoadingSpinner';

import CreateUCD603Form from './formUCD603/CreateUCD603Form';
import ConductWHPER1ExitForm from './ConductWHPER1ExitForm';
import { themeJson } from './FormTheme';

import classes from './formStyles/form.module.css';

const MandatoryForm = () => {
    // HOOKS;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();

    // LOCAL STATE
    const [surveyModel, setSurveyModel] = useState(new Model('{}'));
    const [surveyComplete, setSurveyComplete] = useState(false);
    const [createUCD603Pdf, setCreateUCD603Pdf] = useState(false);
    const [formId, setFormId] = useState('');
    const [adviser, setAdviser] = useState({});
    const [isNavRequestSent, setIsNavRequestSent] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const users = useSelector(selectUsersForNames);
    const usersByLoggedInUserServiceIds = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIds
    );
    const currentForm = useSelector((state) => state.entities.participantService.currentForm);
    const formStatusDetails = useSelector(
        (state) => state.entities.directusService.formStatusDetails
    );
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadCommunicationTemplates());
        dispatch(loadFormTypes());
        if (formStatusDetails?.length < 1) dispatch(loadFormStatusDetails());
        dispatch(loadForms());
        dispatch(loadFormContractByContract(currentParticipant.contractId));
    }, []);

    useEffect(() => {
        if (state && 'formId' in state) {
            setFormId(state.formId);
            dispatch(loadForm(state.formId));
        }
    }, [state]);

    useEffect(() => {
        if (Object.keys(currentForm).length < 1) return;
        const model = new Model(currentForm.data);
        model.applyTheme(themeJson);
        model.showQuestionNumbers = true;
        setSurveyModel(model);
    }, [currentForm]);

    useEffect(() => {
        if (surveyComplete) {
            if (currentForm.name.includes('603')) setCreateUCD603Pdf(true);
        }
    }, [surveyComplete]);

    useEffect(() => {
        if (!('userId' in currentParticipant)) return;
        const adviser = users.find((el) => el.id === currentParticipant?.userId);
        if (!adviser) dispatch(loadUsersMinimalDetails([currentParticipant.userId]));
        else setAdviser(adviser);
    }, [users, currentParticipant?.userId]);

    useEffect(() => {
        if (usersByLoggedInUserServiceIds.length) return;
        if ('serviceIds' in loggedInUser)
            loggedInUser.serviceIds.length > 0 &&
                dispatch(searchUsersByLoggedInUserServiceIds(loggedInUser.serviceIds, 0));
    }, [usersByLoggedInUserServiceIds]);

    useEffect(() => {
        if (isNavRequestSent) {
            if (!(currentParticipant.contractId in contractDetailsById))
                dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
            if (!(currentParticipant.contractId in servicesByContractId))
                dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
        }
    }, [isNavRequestSent]);

    useEffect(() => {
        if (
            isNavRequestSent &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        )
            navigate('/edit_participant');
    }, [contractDetailsById, servicesByContractId, isNavRequestSent]);

    // HELPER FNS
    const loadingError = () => {
        if (Object.keys(currentForm)?.length < 1) return 'No current form found';
        if (formStatusDetails?.length < 1) return 'No form status details';
    };

    // EVENT HANDLERS
    const onComplete = () => {
        createUCD603Pdf && setCreateUCD603Pdf(false);
        setIsNavRequestSent(true);
    };

    const onSurveyComplete = () => setSurveyComplete(true);

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={classes.forms}>
            {currentForm.name.includes('603') && (
                <Survey model={surveyModel} onComplete={onSurveyComplete} />
            )}

            {createUCD603Pdf && adviser ? (
                <CreateUCD603Form
                    currentForm={currentForm}
                    surveyModelData={surveyModel.data}
                    participantsAdviser={adviser}
                    generate={true}
                    onClose={onComplete}
                />
            ) : currentForm.name.includes('WHPER1') ? (
                <ConductWHPER1ExitForm
                    formId={formId}
                    surveyModelData={surveyModel.data}
                    onClose={onComplete}
                    roles={loggedInUser.roles}
                />
            ) : null}

            {!currentForm.name.includes('603') && !currentForm.name.includes('WHPER1') && (
                <div>Incorrect form supplied</div>
            )}
        </div>
    );
};

export default MandatoryForm;
